// src/components/Details.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../CSS/Details.css';
import apiClient from '../lib/api-client';
import Logo from '../assets/icon.svg';
import { auth } from '../lib/firebase'; // Import Firebase auth
import Select from 'react-select';
import { useAuth } from '../AuthContext';



const Details = () => {
    const navigate = useNavigate();
    const { checkAuth } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        company_name: '',
        company_role: '',
        is_promotional_subscribed: false
    });

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const isConfirmed = window.confirm(
            "Please confirm that your details are accurate  \n\n" +
            "Once submitted, changes can only be made by contacting support."
        );


        // Only proceed if user confirmed
        if (!isConfirmed) {
            return;
        }

        const token = localStorage.getItem('access_token');
        if (!token) {
            alert('Session expired. Please login again.');
            navigate('/auth');
            return;
        }

        setIsLoading(true);

        try {
            const requestBody = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                company_name: formData.company_name,
                company_role: formData.company_role,
                is_promotional_subscribed: formData.is_promotional_subscribed,
            };

            await apiClient.setAccessToken(token);

            const data = await apiClient.saveUserDetails(requestBody);

            // Re-check auth state so AuthContext knows profile is now complete
            await checkAuth();

            // Redirect to FEMA report consent step
            navigate('/fema-report-consent', { replace: true });

        } catch (error) {
            console.error('Error:', error);
            alert(error.message || 'Failed to save your details. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);
            // Sign out from Firebase
            await auth.signOut();
            // Sign out from your backend
            await apiClient.logout();

            // Clear local storage
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('user_tier');

            // Redirect to login page
            navigate('/auth');
        } catch (error) {
            console.error('Logout failed:', error);
            alert('Logout failed. Please try again.');
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <div className="auth-container d-flex align-items-center justify-content-center">
            <div className="auth-card" style={{ maxWidth: '600px' }}>

                <button
                    className="btn btn-sm btn-outline-primary position-absolute"
                    style={{ top: '15px', right: '15px' }}
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                >
                    {isLoggingOut ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            <span className="d-none d-sm-inline">Logging out...</span>
                        </>
                    ) : (
                        <>
                            <i className="fas fa-sign-out-alt me-1"></i>
                            <span className="d-none d-sm-inline">Logout</span>
                        </>
                    )}
                </button>

                {/* Header */}
                <div className="text-center mb-4 p-4">
                    <img src={Logo} alt="RECOVER Logo" className="mb-4" style={{ height: '80px' }} />
                    <h2 className="text-primary">Complete Your Profile</h2>
                    <p className="text-muted">Please provide your details to continue</p>
                </div>

                {/* Form */}
                <div className="p-4">
                    <form id="userDetailsForm" onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="first_name" className="form-label">First Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="last_name" className="form-label">Last Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="company_name" className="form-label">Company/Organisation Name</label>
                            <input
                                type="text"
                                className="form-control"
                                id="company_name"
                                placeholder="Enter your company or organisation name"
                                value={formData.company_name}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label htmlFor="company_role" className="form-label">Role</label>
                            <input
                                type="text"
                                className="form-control"
                                id="company_role"
                                placeholder="e.g. Software Engineer, Project Manager"
                                value={formData.company_role}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="is_promotional_subscribed"
                                    checked={formData.is_promotional_subscribed}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        is_promotional_subscribed: e.target.checked
                                    }))}
                                />
                                <label className="form-check-label" htmlFor="is_promotional_subscribed">
                                    Do you want to receive promotional mails?
                                </label>
                            </div>
                            <small className="text-muted d-block mt-1">
                                We will send you performance details of the forecasts and alerts to help you stay informed.
                            </small>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Processing...
                                </>
                            ) : (
                                'Continue'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Details;